import { computed, onBeforeUnmount, onMounted, ref, toValue, unref, watch } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';

import { useEventHub } from '@satellite/composables/useEventHub';
import { yardQueryKeys } from './yard/yardQueryKeys';
import assetVisitService from '@satellite/services/asset-visit-service';

import type { ComputedRef, Ref } from 'vue';
import type { Maybe } from '@nova/core';

export interface YardAsset {
  id: string;
  code?: string;
  type?: string;
  assetVisit?: {
    visitType?: string;
    spotAssignments?: Array<{ spotId?: string }>;
  };
  spotAssignments?: Array<{ spotId?: string }>;
}

export interface UseYardAssetPresenterV2Options {
  /**
   * Asset (container) ID to fetch. Can be a ref for reactivity.
   * Optional if asset (with id) is provided.
   */
  assetId?: Ref<Maybe<string>> | ComputedRef<Maybe<string>>;

  /**
   * Optional asset object. Used as placeholderData while the query loads/refetches.
   * Effective id is derived from assetId or asset.id (explicit assetId wins; mismatch triggers a warning).
   */
  asset?: Ref<Maybe<YardAsset>> | ComputedRef<Maybe<YardAsset>> | YardAsset;

  /**
   * If true, subscribes to websocket events and invalidates query on changes.
   * Default: true
   */
  live?: boolean;

  /**
   * Stale time in milliseconds.
   * Default: Infinity (never stale, until a Subspace event invalidates the query; manual refresh is always available)
   */
  staleTime?: number;

  /**
   * Callback for when asset is updated.
   */
  onUpdateReceived?: (asset: YardAsset) => void;
}

export function useYardAssetPresenterV2(options: UseYardAssetPresenterV2Options) {
  const {
    assetId,
    asset: assetOption,
    live = true,
    staleTime = Infinity,
    onUpdateReceived
  } = options;

  const eventHub = useEventHub();
  const queryClient = useQueryClient();

  const effectiveAssetId = computed((): string | null => {
    const explicitId = unref(assetId);
    const fromObject = toValue(assetOption)?.id ?? null;

    if (explicitId) {
      if (fromObject && fromObject !== explicitId) {
        console.warn(
          '[useYardAssetPresenterV2] assetId mismatch:',
          `explicit assetId (${explicitId}) does not match asset.id (${fromObject}). Using explicit assetId.`
        );
      }
      return explicitId;
    }
    return fromObject;
  });

  const assetPlaceholder = computed(() => toValue(assetOption) ?? undefined);

  const isInViewport = ref(false);
  const pendingInvalidation = ref(false);

  const handleIntersect = (entry?: { isIntersecting?: boolean } | boolean) => {
    const isIntersecting = typeof entry === 'boolean' ? entry : entry?.isIntersecting ?? true;
    isInViewport.value = isIntersecting;

    if (isIntersecting && pendingInvalidation.value) {
      pendingInvalidation.value = false;
      invalidate();
    }
  };

  const assetQuery = useQuery({
    queryKey: computed(() =>
      effectiveAssetId.value ? yardQueryKeys.assetContainer(effectiveAssetId.value) : []
    ),
    queryFn: async ({ signal }) => {
      if (!effectiveAssetId.value) {
        return null;
      }

      const response = await assetVisitService.getAssetContainerByIdV2(effectiveAssetId.value, {
        requestConfig: {
          signal,
          params: {
            s: {
              id: effectiveAssetId.value,
              isActive: true
            },
            join: ['spotAssignments||spotId', 'assetVisit', 'assetVisit.spotAssignments||spotId']
          }
        },
        enableCache: true
      });

      return response as YardAsset;
    },
    enabled: computed(() => Boolean(effectiveAssetId.value) && isInViewport.value),
    placeholderData: assetPlaceholder,
    staleTime,
    refetchOnWindowFocus: false
  });

  const asset = computed((): YardAsset | null => {
    const data = assetQuery.data.value;

    if (!data) {
      return null;
    }

    return {
      id: data.id,
      code: data.code,
      type: data.type,
      assetVisit: data.assetVisit
        ? {
            visitType: data.assetVisit.visitType,
            spotAssignments: data.assetVisit.spotAssignments
          }
        : undefined,
      spotAssignments: data.spotAssignments
    } as YardAsset;
  });

  const assetCode = computed(() => asset.value?.code ?? null);
  const assetType = computed(() => asset.value?.type ?? null);
  const visitType = computed(() => asset.value?.assetVisit?.visitType ?? null);
  const spotId = computed(
    (): Maybe<string> =>
      asset.value?.spotAssignments?.[0]?.spotId ??
      asset.value?.assetVisit?.spotAssignments?.[0]?.spotId ??
      null
  );

  const isLoading = computed(() => assetQuery.isLoading.value);
  const isFetching = computed(() => assetQuery.isFetching.value);
  const error = computed(() => assetQuery.error.value);

  const invalidate = () => {
    if (effectiveAssetId.value) {
      queryClient.invalidateQueries({
        queryKey: yardQueryKeys.assetContainer(effectiveAssetId.value)
      });
    }
  };

  const refresh = async () => {
    await assetQuery.refetch();
  };

  const handleUpdateAssetContainer = (event: { id?: string }) => {
    if (event?.id === effectiveAssetId.value) {
      if (isInViewport.value) {
        invalidate();
      } else {
        pendingInvalidation.value = true;
      }
    }
  };

  const handleUpdateAssetContainerEvent = (event: { assetContainerId?: string }) => {
    if (event?.assetContainerId === effectiveAssetId.value) {
      if (isInViewport.value) {
        invalidate();
      } else {
        pendingInvalidation.value = true;
      }
    }
  };

  watch(asset, newAsset => {
    if (onUpdateReceived && newAsset) {
      onUpdateReceived(newAsset);
    }
  });

  watch(effectiveAssetId, (newId, oldId) => {
    if (newId !== oldId) {
      pendingInvalidation.value = false;
    }
  });

  onMounted(() => {
    if (live) {
      eventHub.on('update-AssetContainer', handleUpdateAssetContainer);
      eventHub.on('update-AssetContainerEvent', handleUpdateAssetContainerEvent);
    }
  });

  onBeforeUnmount(() => {
    if (live) {
      eventHub.off('update-AssetContainer', handleUpdateAssetContainer);
      eventHub.off('update-AssetContainerEvent', handleUpdateAssetContainerEvent);
    }
  });

  return {
    asset,
    assetCode,
    assetType,
    visitType,
    spotId,

    isLoading,
    isFetching,
    error,

    refresh,
    invalidate,

    onIntersect: handleIntersect,

    query: assetQuery
  };
}
